/* eslint-disable @typescript-eslint/no-inferrable-types */
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { IServiceTemplate } from '../../../../core/models/service-template/service-template.model';
import {
  IUiAlertContent,
  ToastService,
  TUiAlertTypes,
  EServicePriceType,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
  IIrembogovBasicLabelKeyPair,
} from '@irembo-andela/irembogov3-common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { EServiceChangeRequestSection } from '../../../../core/models/service-change-request-sections.enum';
import { EServiceChangeRequestStatus } from '../../../../core/models/service-change-request-status.enum';
import { EServicePaymentType } from '../../../../core/models/service-payment-type.enum';
import { EServiceChangeRequestToggleHeaders } from '../../../../core/models/service-change-request-toggle-header-section.enum';
import { JsonEditorComponent, JsonEditorOptions } from '@maaxgr/ang-jsoneditor';

@Component({
  selector: 'irembogov-configuration-pricing-form',
  templateUrl: './configuration-pricing-form.component.html',
  styleUrls: ['./configuration-pricing-form.component.scss'],
})
export class ConfigurationPricingFormComponent
  implements OnChanges, OnInit, AfterViewInit
{
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() defaultData!: Record<string, unknown>;
  @Input() formFields!: any;
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() closeForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() OnPricingFormSubmitted: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Input() isPrice = false;
  @ViewChild(JsonEditorComponent, { static: false })
  jsonEditor!: JsonEditorComponent;
  EServiceChangeRequestSection = EServiceChangeRequestSection;
  EServiceChangeRequestStatus = EServiceChangeRequestStatus;
  EServicePaymentType = EServicePaymentType;
  inReviewMode = false;
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;
  selectedOption: EServiceChangeRequestToggleHeaders =
    EServiceChangeRequestToggleHeaders.WIZARD;
  jsonEditorValidationErrors: ValidationErrors[] = [];

  pricingUpdateValues: Record<string, unknown> = {
    paymentType: null,
    pricing: {},
    paymentExpirationHours: 0,
    paymentAccount: {},
    paymentCode: {},
    paymentMerchant: {},
  };
  isLoading = false;
  showConfigFormErrorMessage = false;
  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  paymentTypeForm: FormGroup = new FormGroup({
    paymentType: new FormControl('', [Validators.required]),
  });
  pricingExpirationForm: FormGroup = new FormGroup({
    paymentExpirationHours: new FormControl('', [Validators.required]),
  });

  headerOptions: IIrembogovBasicLabelKeyPair<EServiceChangeRequestToggleHeaders>[] =
    [
      { label: 'Wizard', key: EServiceChangeRequestToggleHeaders.WIZARD },
      {
        label: 'Code Editor',
        key: EServiceChangeRequestToggleHeaders.CODE_EDITOR,
      },
    ];

  public editorOptions: JsonEditorOptions;
  initialData: Record<string, unknown> = this.defaultData;

  constructor(
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private toastService: ToastService
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.mode = 'code';
    this.editorOptions.modes = ['code'];
  }

  ngOnInit() {
    if (this.defaultData) {
      this.initialData = this.defaultData;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['changeRequest']?.currentValue) {
      this.changeRequest = changes['changeRequest'].currentValue;
      this.inReviewMode =
        this.changeRequest.status ===
        EServiceChangeRequestStatus.IN_REDEVELOPMENT;
    }

    if (changes['defaultData']?.currentValue) {
      this.defaultData = changes['defaultData'].currentValue;
      this.pricingExpirationForm
        .get('paymentExpirationHours')
        ?.setValue(this.defaultData['paymentExpirationHours']);
      this.paymentTypeForm
        .get('paymentType')
        ?.setValue(this.defaultData['paymentType']);
      this.pricingExpirationForm.markAllAsTouched();
      this.pricingExpirationForm.updateValueAndValidity();
      this.pricingUpdateValues = this.defaultData;
    }
  }

  ngAfterViewInit(): void {
    if (this.jsonEditor) {
      this.jsonEditor.setMode('code');

      if (this.defaultData) {
        setTimeout(() => {
          this.jsonEditor.update(this.defaultData as any);
        });
      }
    }
    // this.setupAutoSave();
  }

  async onSubmit() {
    if (this.paymentTypeForm?.controls?.['paymentType']?.value === 'PAID') {
      await this.validateJsonEditor();
    }
    if (!this.isUpdateFieldsValid()) {
      this.toastService.show({
        body: 'Please provide all the required fields',
        type: 'error',
      });
      this.OnPricingFormSubmitted.emit(false);
    } else {
      this.updateFormDisplayedErrorMessageContent(false);
      this.isLoading = true;
      let pricingConfig;
      if (this.selectedOption === EServiceChangeRequestToggleHeaders.WIZARD) {
        this.pricingUpdateValues['paymentType'] =
          this.paymentTypeForm?.get('paymentType')?.value;

        pricingConfig = this.formatPricing(
          this.paymentTypeForm?.get('paymentType')?.value,
          this.pricingUpdateValues
        );
      } else {
        pricingConfig = this.formatPricing(
          this.pricingUpdateValues['paymentType'] as EServicePaymentType,
          this.pricingUpdateValues
        );
      }

      if (this.isTemplateMode && this.template?.id) {
        this.serviceTemplateService
          .updateTemplatePricing(this.template.id, pricingConfig)
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
              this.toastService.show({
                body: 'Template pricing updated successfully',
                type: 'success',
              });
              this.OnPricingFormSubmitted.emit(this.isUpdateFieldsValid());
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update template pricing`
                );
              this.toastService.show({
                body: errorMessage.message,
                type: 'error',
              });
              this.OnPricingFormSubmitted.emit(false);
            },
          });
      } else {
        this.serviceChangeRequestService
          .updateServiceChangeRequestPricing(
            this.changeRequest.id ?? '',
            pricingConfig
          )
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: (res: IHttpSingleDataResponse<IServiceChangeRequest>) => {
              this.toastService.show({
                body: res.responseMessage,
                type: 'success',
              });
              this.OnPricingFormSubmitted.emit(this.isUpdateFieldsValid());
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage: IHttpErrorResponseCodeAndMessage =
                ExtractHttpErrorResponseCodeAndMessage(
                  error,
                  `Failed to update change request pricing`
                );
              this.toastService.show({
                body: errorMessage.message,
                type: 'error',
              });
              this.OnPricingFormSubmitted.emit(false);
            },
          });
      }
    }
  }

  isUpdateFieldsValid(): boolean {
    const paymentType = this.paymentTypeForm.get('paymentType')?.value;

    if (!this.paymentTypeForm.valid) {
      return false;
    }
    if (paymentType === EServicePaymentType.FREE) {
      return true;
    }

    const fieldsToValidate = [
      'pricing',
      'paymentAccount',
      'paymentCode',
      'paymentMerchant',
    ];

    return fieldsToValidate.every(field =>
      this.isPricingFieldValid(this.pricingUpdateValues[field], field)
    );
  }

  onPaymentTypeChange(paymentType: EServicePaymentType) {
    this.paymentTypeForm?.get('paymentType')?.setValue(paymentType);
  }

  onPaymentExpirationChange(event: any) {
    if (event.target.value < 1) {
      event.target.value = 1;
    }
    this.pricingUpdateValues['paymentExpirationHours'] = parseInt(
      event.target.value
    );
  }

  onPriceChange(price: Record<string, unknown>) {
    this.pricingUpdateValues['pricing'] = price;
  }

  onPaymentAccountIdentifierChange(paymentAccount: Record<string, unknown>) {
    this.pricingUpdateValues['paymentAccount'] = paymentAccount;
  }

  onPaymentCodeIdentifierChange(paymentCode: Record<string, unknown>) {
    this.pricingUpdateValues['paymentCode'] = paymentCode;
  }

  onPaymentMerchantChange(paymentMerchant: Record<string, unknown>) {
    this.pricingUpdateValues['paymentMerchant'] = paymentMerchant;
  }

  isPricingFieldValid(pricingField: any, field: string): boolean {
    // If we have a price logic type, just check for the required fields
    if (pricingField?.priceType === 'PRICE_LOGIC') {
      return Boolean(
        pricingField?.priceLogicCode &&
          pricingField?.baseCurrency &&
          Object.keys(pricingField?.priceParameterMapping || {}).length > 0
      );
    }

    // For other price types, use the original logic
    const pricingType =
      pricingField?.dynamicConditions?.length <= 0
        ? EServicePriceType.FIXED
        : EServicePriceType.DYNAMIC;
    switch (pricingType) {
      case EServicePriceType.FIXED:
        if (field === 'pricing') {
          return Boolean(pricingField?.baseCurrency && pricingField?.baseValue);
        }
        return Boolean(pricingField?.baseValue);
      case EServicePriceType.DYNAMIC:
        if (field === 'pricing') {
          return pricingField?.dynamicConditions.every((pricingField: any) => {
            // Check if both currency and value exist and are non-empty
            const hasValidCurrency = this.isNonEmptyValue(
              pricingField?.currency
            );
            const hasValidValue =
              this.isNonEmptyValue(pricingField?.value) ||
              this.isNonEmptyValue(pricingField?.valueAttribute) ||
              this.isNonEmptyValue(pricingField?.valueFormula);

            return hasValidCurrency && hasValidValue;
          });
        }
        return true;
      default:
        return false;
    }
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title: string = '',
    message: string = ''
  ) {
    this.showConfigFormErrorMessage = show;
    this.formAlertContent = { type, title, message };
    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  formatPricing(
    paymentType: EServicePaymentType,
    pricingValues: Record<string, unknown>
  ) {
    switch (paymentType) {
      case EServicePaymentType.PAID:
        return this.formatSenarioDynamicConditions(pricingValues);
      case EServicePaymentType.FREE:
      default:
        return {
          paymentType,
          pricing: null,
          paymentExpirationHours: null,
          paymentAccount: null,
          paymentCode: null,
          paymentMerchant: null,
        };
    }
  }

  formatSenarioDynamicConditions(obj: any): any {
    if (!this.isObject(obj)) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.formatSenarioDynamicConditions(item));
    }

    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (this.isObject(obj[key])) {
          obj[key] = this.formatSenarioDynamicConditions(obj[key]);
        } else if (
          obj[key] === '' ||
          obj[key] === null ||
          obj[key] === undefined
        ) {
          delete obj[key];
        }
      }
    }

    if (this.hasConditionsProperty(obj)) {
      obj['conditions'] = this.filterIncompleteConditions(obj['conditions']);
    }

    return obj;
  }

  hasConditionsProperty(obj: Record<string, unknown>) {
    return (
      Object.prototype.hasOwnProperty.call(obj, 'conditions') &&
      Array.isArray(obj['conditions']) &&
      obj['valueFormula'] !== null
    );
  }

  isObject(value: unknown) {
    return typeof value === 'object' && value !== null;
  }

  isNotEmpty(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  filterIncompleteConditions(conditions: Record<string, unknown>[]) {
    return conditions.filter((condition: Record<string, unknown>) => {
      return (
        this.isNotEmpty(condition['attribute']) &&
        this.isNotEmpty(condition['comparator']) &&
        this.isNotEmpty(condition['value']) &&
        this.isNotEmpty(condition['attributeType'])
      );
    });
  }

  isNonEmptyValue(value: string | number | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    // Handle numbers
    if (typeof value === 'number') {
      return true;
    }

    // Handle strings
    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return false;
  }

  onSelectOptionChange(option: EServiceChangeRequestToggleHeaders) {
    this.selectedOption = option;
    if (option === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      this.jsonEditor.setMode('code');
    }
    this.updatePricingData(option);
  }

  async validateJsonEditor(): Promise<void> {
    const editorJson: any = this.jsonEditor.getEditor();
    this.jsonEditorValidationErrors = await editorJson.validate();
    if (this.jsonEditorValidationErrors.length > 0) {
      return;
    }
    this.defaultData = this.jsonEditor.get() as unknown as Record<
      string,
      unknown
    >;
  }

  updatePricingData(selectedOption: EServiceChangeRequestToggleHeaders) {
    if (selectedOption === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      if (this.defaultData) {
        this.initialData = this.defaultData;
        this.jsonEditor.update(this.initialData as any);
      }
    } else {
      const pricingChanges = this.jsonEditor.get() as unknown as Record<
        string,
        unknown
      >;
      this.defaultData = pricingChanges;
    }
  }
}
